import { z } from 'zod';
import { OptionalCodeSchema } from './common.schema.js';

export const WorksiteTypeSchema = z.enum([
  'OFFICE',
  'CONSTRUCTION',
  'CAMP',
  'RETAIL',
  'WAREHOUSE',
  'REMOTE',
  'OTHER',
]);
export const VerificationModeSchema = z.enum([
  'GPS_ONLY',
  'GPS_AND_IP',
  'GPS_AND_WIFI',
  'IP_ONLY',
  'KIOSK_ONLY',
]);

/**
 * THE SITE TYPES THAT HAVE NO USE FOR IP VERIFICATION, and therefore no use for
 * a verification MODE — they are GPS-only by their nature, not by a setting.
 *
 * The IP allowlist and the mode picker govern a LOGGED-IN employee's web/app
 * self-punch. A construction site and a labour camp are staffed by login-less
 * workers who punch through the field APK, verified by device registration, so
 * there is no browser, no IP to allowlist, and nothing for a mode to select.
 * `WorksiteFormPage` has hidden the whole sub-panel for these two types for
 * exactly that reason.
 *
 * It lives in the shared schema because the rule now has three readers that
 * must agree — the form that hides the panel, the bulk bar that says which
 * selected sites it will skip, and the endpoint that refuses to write them —
 * and a rule spelled out three times is a rule that drifts (§3.2).
 */
export const GPS_ONLY_WORKSITE_TYPES = ['CONSTRUCTION', 'CAMP'] as const;

/** False for a site whose type is GPS-only by nature — see the constant above. */
export function usesIpVerification(type: string): boolean {
  return !(GPS_ONLY_WORKSITE_TYPES as readonly string[]).includes(type);
}

/**
 * True when `value` is a valid IPv4 address or IPv4 CIDR block.
 *
 * Mirrors the parsing in geofence.service `ipInCidr` (IPv4 only, optional
 * `/prefix` in 0–32) so the form rejects exactly the inputs that would later
 * silently fail `ipInCidr` at punch time and block legitimate punches.
 */
export function isIpv4OrCidr(value: string): boolean {
  const [base, prefixStr, ...rest] = value.split('/');
  if (rest.length > 0) return false; // more than one "/"
  if (base === undefined) return false;
  const octets = base.split('.');
  if (octets.length !== 4) return false;
  const validOctet = (oct: string): boolean => {
    // Digits only, 1–3 chars, no leading zeros (Node's net.isIPv4 — which
    // ipInCidr relies on — rejects "010"), value 0–255. Keeping this in lock-
    // step with ipInCidr means the form never accepts an entry that would
    // later silently fail at punch time.
    if (!/^(0|[1-9]\d{0,2})$/.test(oct)) return false;
    return Number(oct) <= 255;
  };
  if (!octets.every(validOctet)) return false;
  if (prefixStr === undefined) return true; // bare address → /32
  if (!/^\d{1,2}$/.test(prefixStr)) return false;
  const prefix = Number(prefixStr);
  return prefix >= 0 && prefix <= 32;
}

const AllowedIpsSchema = z
  .array(
    z
      .string()
      .refine(isIpv4OrCidr, {
        message: 'Must be a valid IPv4 address or CIDR block (e.g. 10.0.0.5 or 192.168.1.0/24)',
      })
  )
  .optional()
  .nullable();

/**
 * THE ERP GENERAL-LEDGER ACCOUNT THIS SITE'S LABOUR IS CHARGED TO.
 *
 * OPTIONAL, AND USUALLY EMPTY ON PURPOSE. The clockcards export DERIVES this
 * from the site's contract code — `docs/erp-clockcardsbatch-export-spec-2026-08-08.md`
 * §4: a project contract is the job's own overhead, the two non-project
 * contracts have their own accounts, "nobody types it". This field is the
 * OVERRIDE for a site the mapping gets wrong or has never seen, so a finance
 * team can move one site's account without waiting for a release.
 *
 * MUST SURVIVE THE ERP'S STRIP UNCHANGED. The ERP removes punctuation and
 * collapses spaces on every input (`sanitiseErpText`), so a code typed with a
 * character it strips would be stored one way here and another way there and
 * could never be matched back. Ten characters is the column: `GL CODE` is
 * CHAR(10) in the ERP's own Usage sheet.
 */
const GlCodeSchema = z
  .string()
  .trim()
  .max(10)
  .regex(/^[^"',?@&!$%^*{}[\]|<>+=/\\\s-]*$/u, {
    message:
      'The ERP strips punctuation and spaces from this column — use letters and digits only.',
  })
  .optional()
  .nullable()
  .transform((v) => (v ? v : null));

export const WorksiteInputSchema = z.object({
  name: z.string().min(1).max(120),
  code: OptionalCodeSchema,
  glCode: GlCodeSchema,
  type: WorksiteTypeSchema.default('OFFICE'),
  address: z.string().max(255).optional().nullable(),
  city: z.string().max(80).optional().nullable(),
  emirate: z.string().max(80).optional().nullable(),
  // ERP-imported sites can arrive without coordinates (latitude/longitude NULL).
  // Keep the range bounds when a value IS present, but allow null/undefined so a
  // coord-less site can be saved (e.g. toggling Active) without being corrupted
  // to (0,0). Geofence punch validation already requires real coords elsewhere.
  latitude: z.number().min(-90).max(90).optional().nullable(),
  longitude: z.number().min(-180).max(180).optional().nullable(),
  // 250 m per the 2026-06-23 workday/site spec. A tighter default silently
  // fences sites far inside their real perimeter and rejects legitimate punches
  // as "outside site", so the default must match the spec, not the map pin.
  radiusMeters: z.number().int().min(20).max(5000).default(250),
  polygonGeoJson: z.unknown().optional().nullable(),
  allowedIps: AllowedIpsSchema,
  verificationMode: VerificationModeSchema.default('GPS_ONLY'),
  allowRemotePunch: z.boolean().default(false),
  // Open-roster: field/DC devices at this site accept any active worker (full
  // gallery, off-roster punch counts, first check-in auto-assigns) instead of
  // only the pre-assigned roster. Typically enabled for CAMP / labour-supply
  // sites where workers arrive without a prior assignment.
  openRoster: z.boolean().default(false),
  // Two crews a day, morning and night: the dispatch board gives the site two
  // lanes. Off, the site has one. `nightStartsAt` is when the night crew is
  // due ("HH:MM", company zone) — before it nobody on the night lane is absent.
  // Null follows the company's `defaultWorkday.nightShiftStartTime`.
  twoShifts: z.boolean().default(false),
  nightStartsAt: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM')
    .nullable()
    .default(null),
  isActive: z.boolean().default(true),
  // CAMP-type worksites only: bed / headcount capacity and the work site(s) this
  // camp serves. Ignored (and cleared) by the service for non-CAMP worksites.
  capacity: z.number().int().min(0).max(100000).optional().nullable(),
  servedSiteIds: z.array(z.string().min(1)).optional().nullable(),
});
export type WorksiteInput = z.infer<typeof WorksiteInputSchema>;

export const WorksiteAssignmentInputSchema = z.object({
  employeeId: z.string().min(1),
  isPrimary: z.boolean().default(false),
  effectiveFrom: z.string().datetime().or(z.string().date()).optional(),
});
export type WorksiteAssignmentInput = z.infer<typeof WorksiteAssignmentInputSchema>;

/**
 * PUT /worksites/:id/managers — the site's project managers, as the whole set.
 * A replace, not an add: the form holds the list and sends it, so a removal is
 * the absence of an id. Capped because each one is a recipient of three daily
 * notices; twenty people running one site is a typo, not an org chart.
 */
export const MAX_WORKSITE_MANAGERS = 20;
export const WorksiteManagersInputSchema = z.object({
  employeeIds: z.array(z.string().min(1)).max(MAX_WORKSITE_MANAGERS),
});
export type WorksiteManagersInput = z.infer<typeof WorksiteManagersInputSchema>;

export const DeviceTypeSchema = z.enum(['KIOSK', 'MOBILE', 'WEB']);

export const DeviceInputSchema = z.object({
  name: z.string().min(1).max(120),
  type: DeviceTypeSchema.default('KIOSK'),
  worksiteId: z.string().optional().nullable(),
});
export type DeviceInput = z.infer<typeof DeviceInputSchema>;

export const TerminalVendorSchema = z.enum(['ZKTECO', 'HIKVISION']);

// Registers a standalone hardware terminal (ZKTeco ADMS / Hikvision ISAPI)
// ahead of it ever calling in — unlike a kiosk, these devices don't go
// through a registration-code claim flow, so an admin must pre-register the
// unit's real serial number before it can produce any attendance writes.
export const RegisterTerminalSchema = z.object({
  serialNumber: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  worksiteId: z.string().min(1),
  vendor: TerminalVendorSchema,
  /* THE ADDRESSES THIS TERMINAL MAY PUSH FROM — REQUIRED, and it is the one
     field on this form that is.

     Neither ADMS nor ISAPI carries a token. The serial number was doing the
     whole job of authentication, and it is printed on the chassis at the gate.
     So this list is not hardening: it is the only credential these protocols
     have, and a terminal registered without one accepts attendance writes from
     anyone who can reach the port and read a label.

     It was optional, on the reasoning that leaving it unset is "a choice worth
     making knowingly". That reasoning holds for a terminal already in service —
     `terminalIngest.service.ts` still warns rather than refusing there, because
     silently stopping a site's turnstile is worse than the exposure it closes.
     It does not hold at REGISTRATION, where there is no running gate to break
     and the person filling the form is the one who knows the site's network.

     Same shape and matcher as `Worksite.allowedIps`, because it answers the
     same question. `.min(1)` rather than a non-empty refinement so the message
     arrives on the field rather than on the form. */
  ingestAllowedIps: z
    .array(
      z.string().refine(isIpv4OrCidr, {
        message: 'Must be a valid IPv4 address or CIDR block (e.g. 10.0.0.5 or 192.168.1.0/24)',
      })
    )
    .min(1, 'Name the addresses this terminal pushes from — it has no other credential.'),
});
export type RegisterTerminalInput = z.infer<typeof RegisterTerminalSchema>;

/**
 * MARK (OR UNMARK) A DEVICE AS A FIXED PANEL AT ITS WORKSITE.
 *
 * The body of `PATCH /api/devices/:id/fixed-location`. One required boolean and
 * nothing else — deliberately not folded into a general device PATCH, because
 * this single field changes what the ATTENDANCE ENGINE believes about every
 * punch the device takes (a fixed panel's missing GPS fix stops being flagged
 * `NO_GPS`, on the ground that its worksite IS its location). A field with that
 * reach gets its own route, its own permission and its own audit row rather
 * than riding along in a rename.
 *
 * REQUIRED, not optional-defaulting-to-true. "Turn it off" and "say nothing"
 * are different requests, and a toggle whose off state is unexpressible is a
 * one-way door.
 */
export const DeviceFixedLocationSchema = z.object({
  isFixedLocation: z.boolean(),
});
export type DeviceFixedLocationInput = z.infer<typeof DeviceFixedLocationSchema>;

/**
 * HOW A FIELD TABLET FINDS ITS SITE (owner, 2026-10-08). LOCKED: every punch at
 * its site. AUTO: each punch at whichever of its home site and these extra sites
 * the punch's GPS fix falls in. Twenty is a crew's rotation, not a region: the
 * tablet downloads every listed site's workers.
 */
export const DeviceSiteChoiceSchema = z.object({
  siteMode: z.enum(['LOCKED', 'AUTO']).optional(),
  extraWorksiteIds: z.array(z.string().min(1)).max(20).optional(),
});
export const DeviceSitesSchema = z.object({
  siteMode: z.enum(['LOCKED', 'AUTO']),
  /** Move the tablet's home site. Punches it captured before the move stay at the old one. */
  homeWorksiteId: z.string().min(1).optional(),
  extraWorksiteIds: z.array(z.string().min(1)).max(20).default([]),
});
export type DeviceSitesInput = z.infer<typeof DeviceSitesSchema>;

/**
 * A BULK EDIT OVER SELECTED WORKSITES — the three properties an admin changes
 * across many sites at once and nowhere else.
 *
 * Deliberately NOT "patch anything in bulk". A worksite's name, code, address
 * and coordinates are per-site facts; there is no such thing as setting fifty
 * sites to one address, and an endpoint that would let you is one mis-click
 * from erasing the register. These three are the opposite: a radius policy, a
 * verification policy and an active flag are exactly the things a company
 * decides once and applies to a group.
 *
 * At least one of the three must be present — a request that changes nothing
 * would still write an audit row per site saying it had.
 */
export const WorksiteBulkUpdateSchema = z
  .object({
    ids: z.array(z.string().min(1)).min(1).max(500),
    isActive: z.boolean().optional(),
    /** Same bounds as the per-site form: below 20m a geofence cannot hold a person. */
    radiusMeters: z.number().int().min(20).max(5000).optional(),
    verificationMode: VerificationModeSchema.optional(),
  })
  .refine(
    (v) =>
      v.isActive !== undefined ||
      v.radiusMeters !== undefined ||
      v.verificationMode !== undefined,
    { message: 'Set at least one of isActive, radiusMeters or verificationMode' }
  );
export type WorksiteBulkUpdate = z.infer<typeof WorksiteBulkUpdateSchema>;
